import { ref, readonly, type Ref } from 'vue';
import type { PlayerProfile, AuthError, AuthUser } from '@/types/auth';
import { isPlayerProfile, isAuthUser, createAuthError, isAuthError } from '@/types/auth';
import { AUTH_CONFIG, ENV_CONFIG, UI_CONFIG } from '@/config/constants';
import { useAuth, _updateAuthUser, _clearAuthState } from '@/composables/useAuth';
import STRINGS from '@/constants/strings';

const useMock = AUTH_CONFIG.isMockAuthEnabled();
if (import.meta.env.MODE === 'production' && useMock) {
  throw new Error('Mock auth must not be enabled in production builds');
}

// Module-level singleton state
const profile = ref<PlayerProfile | null>(null);
const isLoading = ref(false);
const profileError = ref<AuthError | null>(null);

// --- Profile API interface ---

interface ProfileApi {
  fetchProfile(id: string): Promise<PlayerProfile>;
  updateName(name: string): Promise<AuthUser>;
  deleteAccount(password: string): Promise<void>;
}

// --- Mock implementation ---

async function mockDelay(): Promise<void> {
  await new Promise(resolve => setTimeout(resolve, AUTH_CONFIG.MOCK_API_DELAY));
}

const mockProfileApi: ProfileApi = {
  async fetchProfile(id: string): Promise<PlayerProfile> {
    await mockDelay();

    const auth = useAuth();
    const user = auth.user.value;
    if (user && user.id === id) {
      return {
        id: user.id,
        name: user.name,
        total_points: 0,
        created_at: new Date().toISOString(),
      };
    }

    throw createAuthError('unknown', 'Player not found');
  },

  async updateName(name: string): Promise<AuthUser> {
    await mockDelay();

    const auth = useAuth();
    const user = auth.user.value;
    if (!user) {
      throw createAuthError('invalid_credentials', 'Not authenticated');
    }

    return { id: user.id, name, emailVerified: user.emailVerified };
  },

  async deleteAccount(_password: string): Promise<void> {
    await mockDelay();

    const auth = useAuth();
    const user = auth.user.value;
    if (!user) {
      throw createAuthError('invalid_credentials', 'Not authenticated');
    }
    // No-op: the composable handles state clearing after this returns
  },
};

// --- Real implementation ---

const realProfileApi: ProfileApi = {
  async fetchProfile(id: string): Promise<PlayerProfile> {
    const response = await fetch(
      `${ENV_CONFIG.getApiBaseUrl()}/api/profile/${encodeURIComponent(id)}`
    );

    if (response.status === 404) {
      throw createAuthError('unknown', 'Player not found');
    }

    if (!response.ok) {
      throw createAuthError('unknown', `Failed to load profile (status ${response.status})`);
    }

    let json: unknown;
    try {
      json = await response.json();
    } catch {
      throw createAuthError('unknown', 'Server returned an invalid response');
    }

    if (!isPlayerProfile(json)) {
      throw createAuthError('unknown', 'Server returned an invalid response');
    }

    return json;
  },

  async updateName(name: string): Promise<AuthUser> {
    const response = await fetch(`${ENV_CONFIG.getApiBaseUrl()}/api/auth/profile/name`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ name }),
    });

    if (response.status === 401) {
      throw createAuthError('invalid_credentials', 'Not authenticated');
    }

    if (response.status === 409) {
      throw createAuthError('name_taken', 'This name is already taken');
    }

    if (response.status === 422) {
      throw createAuthError('unknown', STRINGS.profile.errors.invalidName);
    }

    if (!response.ok) {
      throw createAuthError('unknown', 'Something went wrong. Please try again.');
    }

    let json: unknown;
    try {
      json = await response.json();
    } catch {
      throw createAuthError('unknown', 'Server returned an invalid response');
    }

    if (!isAuthUser(json)) {
      throw createAuthError('unknown', 'Server returned an invalid response');
    }

    return json;
  },

  async deleteAccount(password: string): Promise<void> {
    const response = await fetch(`${ENV_CONFIG.getApiBaseUrl()}/api/auth/account`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ password }),
    });

    if (response.status === 401) {
      throw createAuthError('invalid_credentials', 'Incorrect password');
    }

    if (!response.ok) {
      throw createAuthError('unknown', 'Something went wrong. Please try again.');
    }
  },
};

// --- API selection ---

const api: ProfileApi = useMock ? mockProfileApi : realProfileApi;

// --- Validation ---

function validateName(name: string): AuthError | null {
  const trimmed = name.trim();
  if (trimmed.length < UI_CONFIG.VALIDATION.PLAYER_NAME_MIN_LENGTH) {
    return createAuthError(
      'unknown',
      `Name must be at least ${UI_CONFIG.VALIDATION.PLAYER_NAME_MIN_LENGTH} characters`
    );
  }
  if (trimmed.length > UI_CONFIG.VALIDATION.PLAYER_NAME_MAX_LENGTH) {
    return createAuthError(
      'unknown',
      `Name must be at most ${UI_CONFIG.VALIDATION.PLAYER_NAME_MAX_LENGTH} characters`
    );
  }
  if (!UI_CONFIG.VALIDATION.DISPLAY_NAME_PATTERN.test(trimmed)) {
    return createAuthError('unknown', 'Name must contain only letters and numbers');
  }
  return null;
}

// --- Test helper ---

function resetProfileState(): void {
  if (import.meta.env.MODE === 'production') return;
  profile.value = null;
  isLoading.value = false;
  profileError.value = null;
}

// --- Public composable ---

export function useProfile() {
  async function fetchProfile(id: string): Promise<void> {
    isLoading.value = true;
    profileError.value = null;

    try {
      const result = await api.fetchProfile(id);
      profile.value = result;
    } catch (err: unknown) {
      if (isAuthError(err)) {
        profileError.value = err;
      } else {
        profileError.value = createAuthError(
          'network',
          'Could not connect to the server. Please check your connection.'
        );
      }
      throw profileError.value;
    } finally {
      isLoading.value = false;
    }
  }

  async function updateName(name: string): Promise<void> {
    const validationError = validateName(name);
    if (validationError) {
      profileError.value = validationError;
      throw validationError;
    }

    isLoading.value = true;
    profileError.value = null;

    try {
      const updatedUser = await api.updateName(name.trim());
      _updateAuthUser(updatedUser);
    } catch (err: unknown) {
      if (isAuthError(err)) {
        profileError.value = err;
      } else {
        profileError.value = createAuthError(
          'network',
          'Could not connect to the server. Please check your connection.'
        );
      }
      throw profileError.value;
    } finally {
      isLoading.value = false;
    }
  }

  async function deleteAccount(password: string): Promise<void> {
    isLoading.value = true;
    profileError.value = null;

    try {
      await api.deleteAccount(password);
      _clearAuthState();
    } catch (err: unknown) {
      if (isAuthError(err)) {
        profileError.value = err;
      } else {
        profileError.value = createAuthError(
          'network',
          'Could not connect to the server. Please check your connection.'
        );
      }
      throw profileError.value;
    } finally {
      isLoading.value = false;
    }
  }

  function clearError(): void {
    profileError.value = null;
  }

  return {
    profile: readonly(profile) as Readonly<Ref<PlayerProfile | null>>,
    isLoading: readonly(isLoading) as Readonly<Ref<boolean>>,
    error: readonly(profileError) as Readonly<Ref<AuthError | null>>,
    fetchProfile,
    updateName,
    deleteAccount,
    clearError,
    _resetProfileState: resetProfileState,
  };
}
